import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "@mui/material/styles";

import GlassCard from "./GlassCard";
import SectionTitle from "./SectionTitle";
import AttachmentMovementsDialog from "./AttachmentMovementsDialog";
import UploadLegalFileDocumentDialog from "../../LegalFileAttachment/UploadLegalFileDocumentDialog";
import RetryEscritoButton from "../../LegalFileAttachment/RetryEscritoButton";
import ConfirmDialog from "../../General/Dialog/ConfirmDialog";
import AppDialog from "../../General/Dialog/AppDialog";
import AuctioneerAssignDialog from "../../LegalFileAttachment/AuctioneerAssignDialog";
import {
  deleteLegalFileDocument,
  updateLegalFileDocumentData,
} from "../../../services/LegalFileAttachmentService";
import { useToast } from "../../../context/ToastContext";
import {
  ATTACHMENT_STATUS_LABELS,
  NOTIFICATION_STAGE_LABELS,
  statusChipSx,
  notificationStatusChipSx,
} from "./detailHelpers";

export default function TabAdjuntos({
  attachments,
  documents,
  notificationCertificates,
  lawsuit,
  onPreviewAttachment,
  onPreviewDocument,
  onPreviewCertificate,
  onPreviewLawsuit,
  legalFile,
  onDocumentsChanged,
}) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Corrección de los datos leídos del PDF (ej. martillero)
  const [docToEdit, setDocToEdit] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingData, setSavingData] = useState(false);

  const [docToAssign, setDocToAssign] = useState(null);

  const openEditData = (doc) => {
    // Designación de martillero: se elige el martillero del registro.
    if (doc.document_type === "martillero_designation") {
      setDocToAssign(doc);
      return;
    }
    setDocToEdit(doc);
    setEditValues(Object.fromEntries(doc.data_fields.map((f) => [f.key, f.value ?? ""])));
  };

  const handleSaveData = async () => {
    setSavingData(true);
    try {
      await updateLegalFileDocumentData(docToEdit.id, editValues);
      showToast("Datos actualizados.", "success");
      setDocToEdit(null);
      onDocumentsChanged?.();
    } catch (err) {
      showToast(err?.response?.data?.message ?? "No se pudieron guardar los datos.", "error");
    } finally {
      setSavingData(false);
    }
  };

  const handleDeleteDocument = async () => {
    setDeleting(true);
    try {
      await deleteLegalFileDocument(docToDelete.id);
      showToast("Documento eliminado.", "success");
      setDocToDelete(null);
      onDocumentsChanged?.();
    } catch (err) {
      showToast(err?.response?.data?.message ?? "No se pudo eliminar el documento.", "error");
    } finally {
      setDeleting(false);
    }
  };
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [movementsOpen, setMovementsOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const openMovements = (attachment) => {
    setSelectedAttachment(attachment);
    setMovementsOpen(true);
  };

  const closeMovements = () => {
    setMovementsOpen(false);
    setSelectedAttachment(null);
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Demanda */}
        <GlassCard>
          <SectionTitle
            icon={<InsertDriveFileIcon />}
            title="Título"
            description="Archivo del título asociado al expediente."
          />
          <Divider sx={{ opacity: 0.6, mb: 1.5 }} />
          {!lawsuit ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin título asociado.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                p: 1.2,
                borderRadius: 2.5,
                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900 }} noWrap>
                  {lawsuit.code || "Título"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.7 }}
                  noWrap
                >
                  {lawsuit.type || "—"} ·{" "}
                  {`${lawsuit.first_name || ""} ${lawsuit.last_name || ""}`.trim() ||
                    "Sin nombre"}
                </Typography>
                {lawsuit.address ? (
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.65, display: "block" }}
                  >
                    {lawsuit.address}
                  </Typography>
                ) : null}
              </Box>
              <Tooltip
                title={
                  !hasPermission("ver demandas detalle")
                    ? ""
                    : !lawsuit.has_pdf
                      ? "No tiene archivo registrado: se migró históricamente sin archivo adjunto."
                      : "Ver archivo"
                }
              >
                <span>
                  <IconButton
                    onClick={() => onPreviewLawsuit(lawsuit.id)}
                    disabled={!hasPermission("ver demandas detalle") || !lawsuit.has_pdf}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.04)",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                      flexShrink: 0,
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </GlassCard>

        {/* Cédulas */}
        <GlassCard>
          <SectionTitle
            icon={<AssignmentIcon />}
            title={`Cédulas de notificación (${notificationCertificates.length})`}
            description="Todas las cédulas generadas para este expediente."
          />
          <Divider sx={{ opacity: 0.6, mb: 1.5 }} />
          {!notificationCertificates.length ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin cédulas de notificación.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 1 }}>
              {notificationCertificates.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1.2,
                    borderRadius: 2.5,
                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }} noWrap>
                      {item.code || item.file_name || "Cédula"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.8,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label={
                          NOTIFICATION_STAGE_LABELS[item.stage] ||
                          item.stage ||
                          "—"
                        }
                        sx={{
                          height: 20,
                          borderRadius: 999,
                          bgcolor: "rgba(25,118,210,0.10)",
                          color: "primary.main",
                          fontWeight: 900,
                          "& .MuiChip-label": {
                            px: 0.9,
                            fontSize: 11,
                          },
                        }}
                      />
                      <Chip
                        size="small"
                        label={item.status_text || "—"}
                        sx={notificationStatusChipSx(item.status_text)}
                      />
                    </Box>
                  </Box>
                  <Tooltip title="Ver archivo">
                    <span>
                      <IconButton
                        onClick={() => onPreviewCertificate(item.id)}
                        disabled={
                          !hasPermission(
                            "descargar cedulas notificacion",
                          )
                        }
                        sx={{
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.04)",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                          flexShrink: 0,
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </GlassCard>

        {/* Adjuntos del Expediente */}
        <GlassCard>
          <SectionTitle
            icon={<AssignmentIcon />}
            title={`Adjuntos del Expediente (${documents.length})`}
            description="Documentos propios del expediente utilizados como fuente para escritos."
            right={
              legalFile && hasPermission("cargar documentos expedientes") ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUploadOutlinedIcon />}
                  onClick={() => setUploadOpen(true)}
                  sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
                >
                  Cargar documento
                </Button>
              ) : null
            }
          />
          <Divider sx={{ opacity: 0.6, mb: 1.5 }} />
          {!documents.length ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin adjuntos del expediente.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 1 }}>
              {documents.map((doc) => {
                const dateLabel = doc.created_at
                  ? new Date(doc.created_at).toLocaleDateString("es-AR")
                  : "—";
                const typeLabel = doc.document_type_label || doc.document_type;
                return (
                  <Box
                    key={`doc-${doc.id}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      p: 1.2,
                      borderRadius: 2.5,
                      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900 }} noWrap>
                        {doc.original_name || doc.name}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.8, mt: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          label="VINCULADO"
                          sx={{
                            height: 20,
                            borderRadius: 999,
                            bgcolor: "rgba(34,197,94,0.12)",
                            color: "success.main",
                            fontWeight: 900,
                            "& .MuiChip-label": { px: 0.9, fontSize: 11 },
                          }}
                        />
                        <Chip
                          size="small"
                          label={typeLabel.toUpperCase()}
                          sx={{
                            height: 20,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.06)",
                            fontWeight: 900,
                            "& .MuiChip-label": { px: 0.9, fontSize: 11 },
                          }}
                        />
                        <Chip
                          size="small"
                          label={dateLabel}
                          sx={{
                            height: 20,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.04)",
                            fontWeight: 700,
                            "& .MuiChip-label": { px: 0.9, fontSize: 11 },
                          }}
                        />
                      </Box>
                      {doc.data_fields?.length > 0 && (
                        <Box sx={{ display: "flex", gap: 0.8, mt: 0.6, flexWrap: "wrap" }}>
                          {doc.data_fields.map((f) => (
                            <Chip
                              key={f.key}
                              size="small"
                              variant="outlined"
                              color={f.value ? "primary" : "warning"}
                              label={`${f.label}: ${f.value || "falta dato"}`}
                              sx={{ height: 22, fontWeight: 700, "& .MuiChip-label": { px: 0.9, fontSize: 11.5 } }}
                            />
                          ))}
                          {doc.data_warning && (
                            <Chip
                              size="small"
                              color="warning"
                              label={doc.data_warning}
                              sx={{ height: 22, fontWeight: 700, "& .MuiChip-label": { px: 0.9, fontSize: 11.5 } }}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                    {doc.data_fields?.length > 0 && hasPermission("cargar documentos expedientes") && (
                      <Tooltip title={doc.document_type === "martillero_designation" ? "Elegir martillero" : "Corregir datos leídos"}>
                        <IconButton onClick={() => openEditData(doc)} sx={{ borderRadius: 2, flexShrink: 0 }}>
                          <EditNoteOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Ver archivo">
                      <span>
                        <IconButton
                          onClick={() => onPreviewDocument(doc.id)}
                          disabled={!hasPermission("descargar archivos adjuntos expedientes")}
                          sx={{
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.04)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                            flexShrink: 0,
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {doc.uploaded_by && hasPermission("eliminar documentos expedientes") && (
                      <Tooltip title="Eliminar documento">
                        <IconButton
                          color="error"
                          onClick={() => setDocToDelete(doc)}
                          sx={{ borderRadius: 2, flexShrink: 0 }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </GlassCard>

        {/* Escritos */}
        <GlassCard>
          <SectionTitle
            icon={<FolderOpenRoundedIcon />}
            title={`Escritos (${attachments.length})`}
            description="Archivos adjuntos cargados o generados para el expediente."
          />
          <Divider sx={{ opacity: 0.6, mb: 1.5 }} />
          {!attachments.length ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Sin escritos asociados.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 1 }}>
              {attachments.map((a) => {
                const statusTab8 = a.pivot?.status || a.status;
                return (
                  <Box
                    key={a.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      p: 1.2,
                      borderRadius: 2.5,
                      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900 }} noWrap>
                        {a.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.8,
                          mt: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          size="small"
                          label={
                            ATTACHMENT_STATUS_LABELS[statusTab8] || "—"
                          }
                          sx={statusChipSx(statusTab8)}
                        />
                        {a.type?.name ? (
                          <Chip
                            size="small"
                            label={String(a.type.name).toUpperCase()}
                            sx={{
                              height: 20,
                              borderRadius: 999,
                              bgcolor: "rgba(0,0,0,0.06)",
                              fontWeight: 900,
                              "& .MuiChip-label": {
                                px: 0.9,
                                fontSize: 11,
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                      {["failed", "error"].includes(statusTab8) && a.pivot?.error_message && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 0.6, color: "error.main", wordBreak: "break-word" }}
                        >
                          Motivo: {a.pivot.error_message}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}
                    >
                      {statusTab8 === "failed" && legalFile && (
                        <RetryEscritoButton
                          attachmentId={a.id}
                          legalFileId={legalFile.id}
                          escritoName={a.name}
                          onDone={onDocumentsChanged}
                        />
                      )}
                      <Tooltip title="Ver archivo">
                        <span>
                          <IconButton
                            onClick={() => onPreviewAttachment(a.id)}
                            disabled={
                              !hasPermission(
                                "descargar archivos adjuntos expedientes",
                              )
                            }
                            sx={{
                              borderRadius: 2,
                              bgcolor: "rgba(0,0,0,0.04)",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Movimientos">
                        <span>
                          <IconButton
                            onClick={() => openMovements(a)}
                            sx={{
                              borderRadius: 2,
                              bgcolor: "rgba(0,0,0,0.04)",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                            }}
                          >
                            <TimelineOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </GlassCard>
      </Box>

      <AttachmentMovementsDialog
        open={movementsOpen}
        onClose={closeMovements}
        attachment={selectedAttachment}
      />

      {legalFile && (
        <UploadLegalFileDocumentDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onDone={onDocumentsChanged}
          legalFile={legalFile}
        />
      )}

      <AuctioneerAssignDialog
        open={Boolean(docToAssign)}
        document={docToAssign}
        onClose={() => setDocToAssign(null)}
        onDone={onDocumentsChanged}
      />

      <AppDialog
        open={Boolean(docToEdit)}
        onClose={() => !savingData && setDocToEdit(null)}
        title="Corregir datos del documento"
        maxWidth="xs"
        fullWidth
        actions={
          <>
            <Button onClick={() => setDocToEdit(null)} disabled={savingData}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveData} disabled={savingData} sx={{ fontWeight: 800 }}>
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            Estos datos completan la plantilla del escrito ({docToEdit?.data_fields?.map((f) => f.placeholder).join(", ")}).
          </Typography>
          {docToEdit?.data_fields?.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              size="small"
              fullWidth
              value={editValues[f.key] ?? ""}
              onChange={(e) => setEditValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
            />
          ))}
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={Boolean(docToDelete)}
        onClose={() => setDocToDelete(null)}
        onConfirm={handleDeleteDocument}
        title="Eliminar documento"
        description={`¿Eliminar "${docToDelete?.original_name ?? ""}"? Deja de estar disponible para los escritos.`}
        confirmText="Eliminar"
        confirmColor="error"
        loading={deleting}
      />
    </>
  );
}
